import React, { useState, useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page as PageLayout,
  DataTable,
  EmptyState,
  LoadingOrbit,
  Text,
  Button,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Compass, GithubLogo, Play } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import {
  useExploratoryTests,
  useExploratorySchedules,
  ExploratoryScheduledSection,
  StartExploratoryTestDialog,
  getExploratoryTestStatusMeta,
} from '../../../features/exploratory-tests';
import type {
  ExploratoryTestListItem,
  ExploratoryTestStatus,
} from '../../../features/exploratory-tests';
import { safeHref } from '../../../utils/safeHref';

const ExploratoryTestStatusBadge: React.FC<{ status: ExploratoryTestStatus }> = ({ status }) => {
  const meta = getExploratoryTestStatusMeta(status);
  return <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.badgeVariant)} hideDot />;
};

/**
 * Canonical `owner/repo` shape; guards the href against malformed repo names.
 * `\w.-` covers GitHub's allowed name chars (letters, digits, `_`, `.`, `-`);
 * each segment is capped at GitHub's 100-char limit so a pathological DB value
 * can't expand into an unbounded URL.
 */
const REPO_FULL_NAME_RE = /^[\w.-]{1,100}\/[\w.-]{1,100}$/;

/**
 * Name cell: the PR title for PR-triggered runs, otherwise the run title, or a
 * slice of the instructions when untitled. The PR link itself is the separate
 * icon-only column below.
 */
export const ExploratoryTestNameCell: React.FC<{ test: ExploratoryTestListItem }> = ({ test }) => (
  <Text size="sm" className="truncate">
    {test.githubPrTitle || test.title || test.instructions.slice(0, 80)}
  </Text>
);

/**
 * PR-link cell: PR-triggered runs render an icon-only link out to the GitHub
 * PR; everything else renders nothing. The href is built only from the repo +
 * PR number columns — never from the title.
 */
export const ExploratoryTestPrLinkCell: React.FC<{ test: ExploratoryTestListItem }> = ({
  test,
}) => {
  const { githubRepoFullName, githubPrNumber } = test;
  if (
    !githubRepoFullName ||
    githubPrNumber === null ||
    !REPO_FULL_NAME_RE.test(githubRepoFullName)
  ) {
    return null;
  }
  const href = safeHref(`https://github.com/${githubRepoFullName}/pull/${githubPrNumber}`);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      aria-label={`View ${githubRepoFullName}#${githubPrNumber} on GitHub`}
      title={`View ${githubRepoFullName}#${githubPrNumber} on GitHub`}
      className="inline-flex items-center text-muted-foreground hover:text-primary"
    >
      <GithubLogo size={16} weight="bold" className="shrink-0" />
    </a>
  );
};

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined,
  durationMs: number | null | undefined
): string => {
  if (durationMs) {
    if (durationMs < 1000) return '<1s';
    if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
    if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
    return `${Math.round(durationMs / 3600000)}h`;
  }
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const ms = end.getTime() - start.getTime();
  if (ms < 1000) return '<1s';
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
  return `${Math.round(ms / 3600000)}h`;
};

export function ExploratoryTestsPage() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const searchParams = useSearch({ from: '/exploratory' }) as { page: number; pageSize: number };
  const page = searchParams.page;
  const pageSize = searchParams.pageSize;
  const [dialogOpen, setDialogOpen] = useState(false);

  const handlePageChange = useCallback(
    (page: number) => {
      navigate({
        to: '/exploratory',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page };
          if (page === 1) delete next.page;
          return next;
        },
      });
    },
    [navigate]
  );

  useBreadcrumbs([{ key: 'exploratory', label: 'Exploratory Tests' }]);

  // Functional runs only — UX-flavor runs live in Labs › UX Review.
  const testsQuery = useExploratoryTests({ page, pageSize, flavor: 'functional' });
  const schedulesQuery = useExploratorySchedules();
  const hasSchedules = (schedulesQuery.data?.schedules.length ?? 0) > 0;

  const newTestButton = (size: 'sm' | 'md') => (
    <Button
      size={size}
      variant="primary"
      iconLeft={<Play size={size === 'sm' ? 14 : 16} weight="fill" />}
      onPress={() => setDialogOpen(true)}
    >
      New Exploratory Test
    </Button>
  );

  const columns: ColumnDef<StockFeatures, ExploratoryTestListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <ExploratoryTestStatusBadge status={row.original.status} />,
        meta: { flex: 0.8 },
      },
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => <ExploratoryTestNameCell test={row.original} />,
        meta: { flex: 3 },
      },
      {
        id: 'prLink',
        header: '',
        cell: ({ row }) => <ExploratoryTestPrLinkCell test={row.original} />,
        meta: { flex: 0.3 },
      },
      {
        accessorKey: 'propertyName',
        header: 'Application',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.propertyName}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        accessorKey: 'issuesFound',
        header: 'Issues',
        cell: ({ row }) => (
          <Text
            size="sm"
            className={row.original.issuesFound > 0 ? 'text-destructive' : 'text-muted-foreground'}
          >
            {row.original.issuesFound}
          </Text>
        ),
        meta: { flex: 0.6 },
      },
      {
        accessorKey: 'createdByEmail',
        header: 'Created by',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.createdByEmail ?? '-'}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(
              row.original.startedAt,
              row.original.finishedAt,
              row.original.durationMs
            )}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Started',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.startedAt
              ? new Date(row.original.startedAt).toLocaleString()
              : new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
    ],
    []
  );

  return (
    <PageLayout>
      {testsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : testsQuery.isError ? (
        <EmptyState
          icon={<Compass size={48} />}
          title="Unable to load exploratory tests"
          description={testsQuery.error?.message || 'Please try again or refresh the page.'}
          action={
            <Button variant="outline" size="sm" onPress={() => void testsQuery.refetch()}>
              Try again
            </Button>
          }
        />
      ) : testsQuery.data && testsQuery.data.items.length > 0 ? (
        <>
          <div className="flex justify-end mb-4">{newTestButton('md')}</div>
          <ExploratoryScheduledSection />
          {hasSchedules && (
            <Text weight="semibold" className="mb-2">
              Runs
            </Text>
          )}
          <DataTable
            columns={columns}
            data={testsQuery.data.items}
            aria-label="Exploratory tests table"
            serverPagination={{
              page,
              pageSize,
              pageCount: testsQuery.data.pagination.totalPages,
              totalItems: testsQuery.data.pagination.totalItems,
              onPageChange: handlePageChange,
            }}
            getRowHref={(test) =>
              buildHref({
                to: '/exploratory/$exploratoryTestId',
                params: { exploratoryTestId: test.id },
              })
            }
          />
        </>
      ) : (
        <>
          <ExploratoryScheduledSection />
          <EmptyState
            icon={<Compass size={48} />}
            title="No exploratory tests yet"
            description="Exploratory tests let you give an AI agent specific instructions to test any feature of your app. The agent will follow your instructions, flag issues, and produce a video recording."
            action={newTestButton('sm')}
          />
        </>
      )}

      {dialogOpen && (
        <StartExploratoryTestDialog isOpen={dialogOpen} onOpenChange={setDialogOpen} />
      )}
    </PageLayout>
  );
}
